import { useState } from 'react';

import { useRouter } from 'next/router';
import { createPortal } from 'react-dom';
import { useAccount } from 'wagmi';

import { IRequestNFT } from '@/api/loans/model';
import { UserWalletInfo } from '@/components/widgets/WalletInfo';
import { useAcceptCounterOffer } from '@/hooks/loans/useAcceptCounterOffer';
import { useWalletNFTsSimplehash } from '@/hooks/query/useWalletNFTsSimplehash';
import { useTheme } from '@/hooks/useTheme';
import { useUserBorrower } from '@/hooks/useUserBorrower';
import { ILoanUser } from '@/redux/loans/model';
import { cn } from '@/utils/helper';
import IconClose from 'assets/icons/icon-close-small.svg';
import ImageNFTExample from 'assets/images/nft-example.jpeg';

import type { IMyLoansTableData } from './Table';
// import NFTCard from '../../my-nfts/NFTCard';
import NewNFTCard from '../../my-nfts/NewNFTCard';
import CancelButton, { TOnCancel } from '../CancelButton';

type SortKeys = 'name' | 'floor';
type SortDir = 'ASC' | 'DESC';
interface IOpenOfferLendAcceptionProps {
  loanRequest: IMyLoansTableData['loanRequest'] & {
    collectionId: string;
    user: ILoanUser;
  };
  onCancel?: TOnCancel;
}

const filterKeys = {
  name: {
    ASC: 'transfer_time__asc',
    DESC: 'transfer_time__desc',
  },
  floor: {
    ASC: 'floor_price__asc',
    DESC: 'floor_price__desc',
  },
};

function OpenOfferLendAcception({ loanRequest, onCancel }: IOpenOfferLendAcceptionProps) {
  const router = useRouter();
  const { mode } = useTheme();
  const darkMode = mode === 'dark';
  const sort = ((Array.isArray(router.query.sort) ? router.query.sort[0] : router.query.sort) as SortKeys) || 'floor';
  const dir = ((Array.isArray(router.query.dir) ? router.query.dir[0] : router.query.dir) as SortDir) || 'DESC';
  const order_by = filterKeys[sort]?.[dir] || 'floor_price__desc';
  const { tokenId, loanTokenOwner, collectionId: contractAddress, collection } = loanRequest;
  const { acceptCounterOffer } = useAcceptCounterOffer();
  const [isCollectionOfferModalOpened, setCollectionOfferModal] = useState(false);
  const [selectedNFT, setSelectedNFT] = useState<Pick<IRequestNFT, 'collectionTokenId' | 'imageUrl'> | null>(null);
  const { data } = useWalletNFTsSimplehash({ contractAddress: [contractAddress], order_by, collectionIds: collection?.id });
  const { checkIsBorrower, checkIsLender } = useUserBorrower(false, true);

  const handleCloseModal = () => {
    setCollectionOfferModal(false);
  };

  const { address } = useAccount();

  const acceptCollectionOffer = () => {
    if (!selectedNFT || !address || !collection) {
      return;
    }
    acceptCounterOffer({ ...loanRequest, tokenId: selectedNFT?.collectionTokenId, collectionAddress: collection?.address }, selectedNFT?.collectionTokenId, address, selectedNFT.imageUrl);
  };

  const isNormalLoanRequest = !loanRequest.lenderAddress;

  const isBorrower = checkIsBorrower({ ownerAddress: loanTokenOwner?.ownerAddress, senderId: loanRequest.user.id, lenderAddress: loanRequest.lenderAddress });

  const isLender = checkIsLender({
    lenderAddress: loanRequest.lenderAddress,
    senderId: loanRequest.userId,
  });

  const NFTs = data?.nfts;
  const shouldShowAcceptButton = NFTs?.length && !isLender && isBorrower === false;

  return (
    <>
      {isLender || (isNormalLoanRequest && isBorrower) ? (
        <CancelButton onCancel={onCancel} id={loanRequest.id} />
      ) : shouldShowAcceptButton ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (tokenId && loanTokenOwner?.ownerAddress && loanTokenOwner.collectionAddress) {
              acceptCounterOffer({ ...loanRequest, tokenId, collectionAddress: loanTokenOwner.collectionAddress }, tokenId, loanTokenOwner.ownerAddress);
            } else {
              setCollectionOfferModal(true);
            }
          }}
          className='bg-primary text-white text-xs/3 font-semibold w-[86px] h-[39px] rounded flex justify-center items-center m-auto'
        >
          Accept
        </button>
      ) : null}
      {contractAddress &&
        isCollectionOfferModalOpened &&
        collection &&
        createPortal(
          <div onClick={(e) => e.stopPropagation()} className='w-screen h-screen fixed top-0 z-[101] bg-white dark:!bg-jet-black  flex justify-center items-center'>
            <div className={cn('w-full max-w-[948px] bg-white-100 dark:bg-jet-black-100')}>
              <div className={cn('p-6 flex flex-col sm:flex-row justify-between items-center border dark:border-coal-border border-coal-50')}>
                <div className={cn('font-semibold text-xl/6 dark:text-white text-coal-250')}>Select your {collection.name} NFT for this loan</div>
                <div className='flex items-center gap-4'>
                  <UserWalletInfo className={cn('h-[29px] [&_.text]:!text-coal-300 dark:[&_.text]:!text-coal-50 [&_.text]:!text-[10px]/4  dark:!bg-jet-black !bg-white')} addressClassName='!text-[10px] !text-coal-300 dark:!text-coal-50' />
                  <button onClick={handleCloseModal} className={cn('w-[31px] h-[27px]')}>
                    <IconClose />
                  </button>
                </div>
              </div>
              <div className={cn('grid border-b dark:border-coal-border border-coal-50 grid-cols-2 md:grid-cols-4 max-h-[65vh] overflow-auto')}>
                {NFTs?.map(({ normalized_metadata, token_id, name, rarity_rank, floor_price_usd }) => {
                  const imageUrl = normalized_metadata?.image || ImageNFTExample.src;
                  return (
                    <NewNFTCard
                      key={token_id}
                      asset={{
                        image_url: imageUrl,
                        name,
                        token_id,
                        rarity: rarity_rank,
                        floorPriceInUsd: floor_price_usd,
                      }}
                      className={cn(selectedNFT?.collectionTokenId === token_id && '!border-primary bg-white dark:bg-jet-black [&_.title]:!text-primary [&_.check]:flex')}
                      onClick={() => {
                        if (selectedNFT?.collectionTokenId === token_id) {
                          setSelectedNFT(null);
                        } else {
                          setSelectedNFT({ collectionTokenId: token_id, imageUrl });
                        }
                      }}
                    />
                  );
                })}
              </div>
              <div className='py-5 flex gap-5 justify-center border-x border-b dark:border-coal-border border-coal-50 bg-white-100 dark:bg-jet-black-100'>
                <button onClick={acceptCollectionOffer} disabled={!selectedNFT} className='bg-primary text-sm rounded-sm px-3 py-2 text-white font-bold disabled:opacity-60'>
                  Accept
                </button>
                <button onClick={handleCloseModal} className={cn('text-sm rounded-sm px-3 py-2 font-bold', darkMode ? 'bg-jet-black text-white' : 'bg-light-blue-grey text-dark-gray-100')}>
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
export default OpenOfferLendAcception;
